import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { days, inr, LineItem, monthLabel, PayrollLineDetail } from '../payroll.api';

function ItemsTable({ title, items, total, showFull }: { title: string; items: LineItem[]; total?: number; showFull: boolean }) {
  return (
    <Box sx={{ flex: 1, minWidth: 260 }}>
      <Typography variant='subtitle2' fontWeight={700} sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Component</TableCell>
            {showFull && <TableCell align='right'>Full month</TableCell>}
            <TableCell align='right'>{showFull ? 'Earned' : 'Amount'}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.length === 0 && (
            <TableRow>
              <TableCell {...({ colSpan: showFull ? 3 : 2 } as object)}>
                <Typography variant='body2' color='text.secondary'>
                  None
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {items.map((i) => (
            <TableRow key={i.code}>
              <TableCell>{i.name}</TableCell>
              {showFull && <TableCell align='right'>{inr(i.fullAmount)}</TableCell>}
              <TableCell align='right'>{inr(i.amount)}</TableCell>
            </TableRow>
          ))}
          {total !== undefined && (
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
              {showFull && <TableCell />}
              <TableCell align='right' sx={{ fontWeight: 700 }}>
                {inr(total)}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

/** One employee's month: the days that set the pay, then earnings, deductions and net. */
export default function SalaryBreakdown({ line }: { line: PayrollLineDetail }) {
  const earnings = line.items.filter((i) => i.kind === 'EARNING');
  const deductions = line.items.filter((i) => i.kind === 'DEDUCTION');
  const employer = line.items.filter((i) => i.kind === 'EMPLOYER');

  const facts: [string, string][] = [
    ['Days in month', days(line.daysInMonth)],
    ['Pay days (basis)', days(line.payDays)],
    ['Working days', days(line.workingDays)],
    ['Weekly offs', days(line.weeklyOffs)],
    ['Holidays', days(line.holidays)],
    ['Present', days(line.presentDays)],
    ['Half days', days(line.halfDays)],
    ['Paid leave', days(line.paidLeaveDays)],
    ['Unpaid leave', days(line.unpaidLeaveDays)],
    ['Absent', days(line.absentDays)],
    ['Late marks', days(line.lateMarks)],
    ['Late-mark LOP', days(line.lateLopDays)],
    ['Not employed', days(line.notEmployedDays)],
    ['LOP days', days(line.lopDays)],
    ['Payable days', days(line.payableDays)],
  ];

  return (
    <Stack gap={2.5}>
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' flexWrap='wrap' gap={1}>
        <Stack>
          <Typography variant='h6' fontWeight={700}>
            {line.name} {line.code ? `(${line.code})` : ''}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {[line.designation, line.department].filter(Boolean).join(' · ')} — {monthLabel(line.year, line.month)}
          </Typography>
        </Stack>
        <Chip
          color={line.runStatus === 'PAID' ? 'success' : 'warning'}
          label={line.runStatus === 'PAID' ? 'Paid' : 'Calculated, not paid'}
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 1,
        }}
      >
        {facts.map(([k, v]) => (
          <Box key={k} sx={{ p: 1, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
            <Typography variant='caption' color='text.secondary'>
              {k}
            </Typography>
            <Typography variant='body2' fontWeight={700}>
              {v}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography variant='body2'>
        Monthly gross <b>{inr(line.monthlyGross)}</b> × payable {days(line.payableDays)} ÷ {days(line.payDays)} days = earned gross{' '}
        <b>{inr(line.earnedGross)}</b>
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={3}>
        <ItemsTable title='Earnings' items={earnings} total={line.earnedGross} showFull />
        <ItemsTable title='Deductions' items={deductions} total={line.totalDeductions} showFull={false} />
      </Stack>

      <Divider />
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant='h6'>Net pay</Typography>
        <Typography variant='h5' fontWeight={800} color={line.netPay < 0 ? 'error' : 'success.main'}>
          {inr(line.netPay)}
        </Typography>
      </Stack>

      {employer.length > 0 && (
        <ItemsTable title='Employer contributions (paid by the company, not deducted)' items={employer} showFull={false} />
      )}

      <Typography variant='caption' color='text.secondary'>
        {[
          line.pan ? `PAN ${line.pan}` : null,
          line.uanNo ? `UAN ${line.uanNo}` : null,
          line.esicNo ? `ESIC ${line.esicNo}` : null,
          line.paymentMode ? `Paid by ${line.paymentMode}` : null,
          line.bankName ? line.bankName : null,
          line.bankAccountNo ? `A/c ${line.bankAccountNo}` : null,
          line.bankIfsc ? `IFSC ${line.bankIfsc}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Typography>
      {line.remarks && (
        <Typography variant='body2' color='warning.main'>
          {line.remarks}
        </Typography>
      )}
    </Stack>
  );
}
